import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Target, TrendingUp, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RepDailyScoreCard } from '@/components/RepDailyScoreCard';
import { VoiceNoteDock } from '@/components/VoiceNoteDock';
import { useAuthStore } from '@/stores/authStore';

interface OpportunitySummary {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: string | number;
  forecastClassification: string;
  expectedCloseDate: string | null;
  currentStageId: string;
  assignedRepId: string | null;
}

interface InsightSnapshot {
  payloadJson: string;
  createdAt: string | null;
}

interface OpportunityInsights {
  revenuePrediction: InsightSnapshot | null;
  dealStrategy: InsightSnapshot | null;
}

interface RankedDeal {
  opp: OpportunitySummary;
  closeProbability: number | null;
  nextAction: string | null;
  urgency: string | null;
  drivers: string[];
  score: number;
  daysSinceUpdate: number | null;
}

function safeParse<T>(json: string | undefined): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Story 58.1 — Rep Action Board ("The Closer").
 *
 * A ranked queue of the rep's open opportunities, scored by
 *   A11 close probability × deal value × time-decay (older deals
 *   surface higher because they need attention)
 *
 * Each row shows the headline (A12 next-best action) so the rep can
 * act without clicking through. Click navigates to the opp detail.
 */
export function CockpitRepPage() {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId);
  const [deals, setDeals] = useState<RankedDeal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<OpportunitySummary[]>('/opportunities', {
        params: { status: 'OPEN', assignedRepId: userId },
      })
      .then(async ({ data }) => {
        const enriched = await Promise.all(
          data.map(async (opp) => {
            try {
              const { data: insights } = await apiClient.get<OpportunityInsights>(
                `/opportunities/${opp.id}/insights`,
              );
              const a11 = safeParse<{ closeProbability?: number; drivers?: string[] }>(
                insights.revenuePrediction?.payloadJson,
              );
              const a12 = safeParse<{ action?: string; urgency?: string }>(
                insights.dealStrategy?.payloadJson,
              );
              return enrich(opp, a11, a12);
            } catch {
              return enrich(opp, null, null);
            }
          }),
        );
        if (cancelled) return;
        // Score = (closeProbability ?? 30) × value × (1 + daysSinceUpdate/30)
        // — older deals float up so the rep is forced to act on them.
        enriched.sort((a, b) => b.score - a.score);
        setDeals(enriched);
      })
      .catch(() => setDeals([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center gap-3">
        <Target className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">{t('cockpitRep.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('cockpitRep.subtitle')}</p>
        </div>
      </header>

      <RepDailyScoreCard />

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : deals.length === 0 ? (
        <Card>
          <p className="text-sm text-muted-foreground">{t('cockpitRep.empty')}</p>
        </Card>
      ) : (
        <ol className="space-y-2">
          {deals.map((d, idx) => (
            <li key={d.opp.id}>
              <Link
                to={`/opportunities/${d.opp.id}`}
                className="block rounded-lg border bg-card p-4 transition hover:bg-muted/30"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{d.opp.name}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                        {fmtMoney(Number(d.opp.estimatedDealValue))}
                      </span>
                      {d.closeProbability !== null && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            d.closeProbability >= 60
                              ? 'bg-emerald-900/40 text-emerald-200'
                              : d.closeProbability >= 30
                                ? 'bg-amber-900/40 text-amber-200'
                                : 'bg-rose-900/40 text-rose-200'
                          }`}
                        >
                          <TrendingUp className="mr-1 inline h-3 w-3" />
                          {d.closeProbability}%
                        </span>
                      )}
                      {d.daysSinceUpdate !== null && d.daysSinceUpdate > 14 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-900/40 px-2 py-0.5 text-xs text-rose-200">
                          <AlertTriangle className="h-3 w-3" />
                          {d.daysSinceUpdate}d stale
                        </span>
                      )}
                    </div>
                    {d.nextAction && (
                      <p className="mt-2 text-sm">
                        <span className="font-medium text-amber-200">→ </span>
                        {d.nextAction}
                        {d.urgency && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({d.urgency})
                          </span>
                        )}
                      </p>
                    )}
                    {d.drivers.length > 0 && (
                      <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                        {d.drivers.slice(0, 2).map((dr, i) => (
                          <li key={i}>{dr}</li>
                        ))}
                      </ul>
                    )}
                    {!d.nextAction && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {t('cockpitRep.no_insights')}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}

      {!loading && deals.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <Button asChild size="sm" variant="link">
            <Link to="/opportunities">{t('cockpitRep.see_all')}</Link>
          </Button>
        </p>
      )}

      <VoiceNoteDock
        openOpps={deals.map((d) => ({ id: d.opp.id, name: d.opp.name }))}
      />
    </div>
  );
}

function enrich(
  opp: OpportunitySummary,
  a11: { closeProbability?: number; drivers?: string[] } | null,
  a12: { action?: string; urgency?: string } | null,
): RankedDeal {
  const probability = a11?.closeProbability ?? null;
  const value = Number(opp.estimatedDealValue) || 0;
  // No reliable last-update timestamp on the OpportunitySummary; the
  // ranking treats stale-detection as best-effort. Real implementation
  // pulls updated_at when 58.1 v2 ships.
  const daysSinceUpdate = null;
  const score = (probability ?? 30) * value;
  return {
    opp,
    closeProbability: probability,
    nextAction: a12?.action ?? null,
    urgency: a12?.urgency ?? null,
    drivers: a11?.drivers ?? [],
    score,
    daysSinceUpdate,
  };
}
